import { apiSlice } from './apiSlice';

/**
 * Competitions. Every path carries a trailing slash — without one Django's
 * APPEND_SLASH 301s to the same URL and each call costs two round-trips.
 *
 *   GET /v2/competition/overview/    → { stats, lists: { live, joined, upcoming, ended } }
 *     ?tab=joined|upcoming|ended|live → the same, narrowed to the named list
 *   GET /v2/competition/detail/      → the trader's own competition + their stats
 *   GET /v2/competition/leaderboard/ → { competition, user, leaderboard: { meta, results } }
 *   GET /v2/competition/rules/       → { rules[], awards[] }
 *   POST /v2/competition/participate/ ← { competition_id, name, pwd, basicDetails }
 *
 * The leaderboard call is also the detail call: it answers with the whole
 * competition record — description, prizes, entry conditions, a server-side
 * countdown — none of which a list row carries.
 */
export const competitionsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * The list page: the banner's figures, and the list behind the tab named
     * by `tab` (`joined` | `upcoming` | `ended` | `live`). Which tab is the
     * server's question to answer — `joined` is a membership fact no filter
     * over a list of competitions could recover — so a tab switch is a
     * request, and each tab is its own cache entry.
     */
    getCompetitionOverview: builder.query({
      query: (params) => ({
        url: '/v2/competition/overview/',
        params,
      }),
      providesTags: ['Competition'],
    }),

    /**
     * The Overview hero: the competition the trader is in right now, and how
     * they are doing in it — the per-trader figures (open trades, the W/L
     * split, drawdown headroom, gross P/L, best and worst trade) that neither
     * a list card nor a leaderboard row carries. Takes no params; the bearer
     * token is the query.
     */
    getCompetitionDetail: builder.query({
      query: () => '/v2/competition/detail/',
      providesTags: ['Competition'],
    }),

    /** `competition_id`, and the `search` / `filter` / `limit` that `meta` echoes back. */
    getCompetitionLeaderboard: builder.query({
      query: (params) => ({
        url: '/v2/competition/leaderboard/',
        params,
      }),
      providesTags: ['Competition'],
    }),

    /** The rule text and the full award table — not part of the detail payload. */
    getCompetitionRules: builder.query({
      query: () => ({
        url: '/v2/competition/rules/',
      }),
      providesTags: ['Competition'],
    }),

    /**
     * Entering a competition. `name` is the trader's own, off their profile —
     * the form fills it rather than asking — while `basicDetails` (the
     * nickname the board shows them by) and `pwd` are theirs to type.
     *
     * Invalidating `Competition` is what flips the page over: the detail call
     * refetches, comes back with the trader on the board, and the entry form
     * gives way to its entered state without a reload. It also refreshes the
     * list page's Joined tab behind them.
     */
    participateInCompetition: builder.mutation({
      query: (body) => ({
        url: '/v2/competition/participate/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Competition'],
    }),
  }),
});

export const {
  useGetCompetitionOverviewQuery,
  useGetCompetitionDetailQuery,
  useGetCompetitionLeaderboardQuery,
  useGetCompetitionRulesQuery,
  useParticipateInCompetitionMutation,
} = competitionsApi;
